import { useAtom } from "jotai";
import { useFormContext } from "react-hook-form";

import { currentStep } from "@/app/store";
import { addMoneySimbols } from "@/app/utils/formatMoneyValue";
import masks from "@/app/utils/masks";
import { Button } from "@/view/components/ui/button";

type Offer = {
  prazo: number;
  valorParcela: number;
  valorSolicitado: number;
  vencimento: string;
} | null;

interface ReviewProposalProps {
  visible?: boolean;
  offer: Offer;
  isSubmitting: boolean;
  onSubmit: () => void;
}

const formatDate = (value?: string) => {
  if (!value) return "-";

  const [year, month, day] = value.slice(0, 10).split("-");

  return `${day}/${month}/${year}`;
};

const Row = ({ label, value }: { label: string; value: string }) => (
  <li className="flex items-baseline justify-between font-medium">
    <p className="text-xs text-black04">{label}</p>
    <strong className="text-sm text-black01">{value}</strong>
  </li>
);

export default function ReviewProposal({
  visible,
  offer,
  isSubmitting,
  onSubmit,
}: ReviewProposalProps) {
  const [, setStep] = useAtom(currentStep);
  const { getValues } = useFormContext();

  const values = getValues();

  return (
    <div className={visible ? "mt-4 flex flex-col gap-8" : "hidden"}>
      <div className="text-black01">
        <p className="text-sm font-medium">Valor solicitado</p>
        <h3 className="text-3xl font-semibold">
          {addMoneySimbols(offer?.valorSolicitado)}
        </h3>
      </div>

      <div className="flex max-h-[320px] flex-col gap-4 overflow-y-auto border-b pb-4">
        <div>
          <div className="mb-2 flex items-center gap-3">
            <p className="text-sm font-semibold text-black01">Operação</p>
            <div className="h-px flex-1 bg-gray01" />
          </div>
          <ul className="flex flex-col">
            <Row label="Prazo" value={`${offer?.prazo ?? "-"}x`} />
            <Row
              label="Valor da parcela"
              value={addMoneySimbols(offer?.valorParcela)}
            />
            <Row
              label="Primeiro vencimento"
              value={formatDate(offer?.vencimento)}
            />
          </ul>
        </div>

        <div>
          <div className="mb-2 flex items-center gap-3">
            <p className="text-sm font-semibold text-black01">Dados pessoais</p>
            <div className="h-px flex-1 bg-gray01" />
          </div>
          <ul className="flex flex-col">
            <Row label="Nome" value={values?.cliente?.nome || "-"} />
            <Row label="RG" value={values?.cliente?.rg || "-"} />
            <Row label="Nome da mãe" value={values?.cliente?.nomeMae || "-"} />
            <Row
              label="Telefone"
              value={masks.phone(values?.cliente?.telefone || "")}
            />
          </ul>
        </div>

        <div>
          <div className="mb-2 flex items-center gap-3">
            <p className="text-sm font-semibold text-black01">Endereço</p>
            <div className="h-px flex-1 bg-gray01" />
          </div>
          <ul className="flex flex-col">
            <Row
              label="Logradouro"
              value={`${values?.endereco?.logradouro || "-"}, ${
                values?.endereco?.numero || "-"
              }`}
            />
            <Row label="Bairro" value={values?.endereco?.bairro || "-"} />
            <Row
              label="Cidade"
              value={`${values?.endereco?.cidade || "-"} - ${
                values?.endereco?.uf || "-"
              }`}
            />
          </ul>
        </div>

        <div>
          <div className="mb-2 flex items-center gap-3">
            <p className="text-sm font-semibold text-black01">Dados bancários</p>
            <div className="h-px flex-1 bg-gray01" />
          </div>
          <ul className="flex flex-col">
            <Row label="Banco" value={values?.bancario?.bancoId || "-"} />
            <Row label="Agência" value={values?.bancario?.agencia || "-"} />
            <Row label="Conta" value={values?.bancario?.numero || "-"} />
          </ul>
        </div>
      </div>

      <div className="flex justify-end gap-5">
        <Button
          type="button"
          className="rounded-full bg-white02 px-8 py-3 font-semibold text-primary hover:bg-white02"
          onClick={() => setStep(0)}
          disabled={isSubmitting}
        >
          Editar dados
        </Button>
        <Button
          type="button"
          className="rounded-full px-8 py-3 font-semibold text-white"
          onClick={onSubmit}
          isLoading={isSubmitting}
          disabled={isSubmitting}
        >
          Enviar proposta
        </Button>
      </div>
    </div>
  );
}
